import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { jwtDecode } from "jwt-decode";
import posthog from 'posthog-js';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// --- Device ID (persistent per-browser fingerprint for session enforcement) ---
function getDeviceId() {
    let deviceId = localStorage.getItem('stratiq_device_id');
    if (!deviceId) {
        deviceId = crypto.randomUUID();
        localStorage.setItem('stratiq_device_id', deviceId);
    }
    return deviceId;
}

// Global fetch interceptor to automatically attach X-Device-ID to all /api/ requests
if (typeof window !== 'undefined' && !window._fetchDeviceInterceptorInstalled) {
    window._fetchDeviceInterceptorInstalled = true;
    const originalFetch = window.fetch;
    window.fetch = async function (resource, config = {}) {
        const url = typeof resource === 'string' ? resource : resource?.url || '';
        
        // Attach X-Device-ID to all /api/ requests automatically
        if (url.includes('/api/')) {
            const deviceId = getDeviceId();
            const headers = new Headers(config?.headers || {});
            if (!headers.has('X-Device-ID')) {
                headers.set('X-Device-ID', deviceId);
            }
            config = { ...config, headers };
        }

        const response = await originalFetch(resource, config);

        // Intercept session conflict and warning headers globally
        if (url.includes('/api/')) {
            if (response.status === 403) {
                response.clone().json().then(data => {
                    if (data?.detail && typeof data.detail === 'string' && data.detail.startsWith('SESSION_CONFLICT')) {
                        window.dispatchEvent(new CustomEvent('stratiq-session-conflict'));
                    }
                }).catch(() => {});
            } else {
                const warning = response.headers.get('X-Session-Warning');
                if (warning === 'true') {
                    const seconds = parseInt(response.headers.get('X-Session-Warning-Seconds') || '60', 10);
                    window.dispatchEvent(new CustomEvent('stratiq-session-warning', { detail: { secondsRemaining: seconds } }));
                }
            }
        }
        return response;
    };
}

export function AuthProvider({ children }) {
    const [token, setToken] = useState(localStorage.getItem('stratiq_token'));
    const [user, setUser] = useState(() => {
        const storedUser = localStorage.getItem('stratiq_user');
        if (storedUser) {
            try {
                return JSON.parse(storedUser);
            } catch (e) {
                return null;
            }
        }
        return null;
    });
    const [isLoading, setIsLoading] = useState(true);
    const [sessionConflict, setSessionConflict] = useState(false);
    const [sessionWarning, setSessionWarning] = useState(null); // { secondsRemaining: N }
    const warningTimeoutRef = useRef(null);

    const deviceId = getDeviceId();

    // Runtime Config (window.config) > Build-time Env (import.meta.env)
    const runtimeConfig = window.config || {};
    const GOOGLE_CLIENT_ID = runtimeConfig.GOOGLE_CLIENT_ID || import.meta.env.VITE_GOOGLE_CLIENT_ID;
    const POSTHOG_KEY = runtimeConfig.POSTHOG_KEY || import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
    const POSTHOG_HOST = runtimeConfig.POSTHOG_HOST || import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

    if (!GOOGLE_CLIENT_ID) {
        console.error("CRITICAL: VITE_GOOGLE_CLIENT_ID is missing from environment variables.");
    }

    // Initialize PostHog (Idempotent with guard)
    useEffect(() => {
        const isAlreadyInitialized = posthog.__loaded || !!posthog.persistence || !!posthog.config;

        if (!window._posthogInitialized && !isAlreadyInitialized && POSTHOG_KEY && POSTHOG_HOST) {
            try {
                posthog.init(POSTHOG_KEY, {
                    api_host: POSTHOG_HOST,
                    person_profiles: 'identified_only',
                });
                window._posthogInitialized = true;
            } catch (err) {
                console.warn("PostHog initialization skipped or failed:", err);
            }
        } else if (isAlreadyInitialized) {
            window._posthogInitialized = true;
        }
    }, [POSTHOG_KEY, POSTHOG_HOST]);

    // --- Global Session Event Listeners ---
    useEffect(() => {
        const handleConflictEvent = () => {
            console.warn('[SessionEnforcement] Session conflict event received — triggering modal');
            setSessionConflict(true);
        };
        const handleWarningEvent = (e) => {
            const seconds = e.detail?.secondsRemaining || 60;
            setSessionWarning({ secondsRemaining: seconds });
        };

        window.addEventListener('stratiq-session-conflict', handleConflictEvent);
        window.addEventListener('stratiq-session-warning', handleWarningEvent);

        return () => {
            window.removeEventListener('stratiq-session-conflict', handleConflictEvent);
            window.removeEventListener('stratiq-session-warning', handleWarningEvent);
        };
    }, []);

    // --- Session warning auto-dismiss ---
    useEffect(() => {
        if (sessionWarning) {
            // Auto-dismiss warning after the remaining grace period
            if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
            warningTimeoutRef.current = setTimeout(() => {
                setSessionWarning(null);
                // Grace period expired — the next API call will trigger the hard 403
            }, (sessionWarning.secondsRemaining || 60) * 1000);
        }
        return () => {
            if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
        };
    }, [sessionWarning]);

    /**
     * Check API response for session enforcement headers/status.
     * Called after every authenticated fetch.
     */
    const checkSessionResponse = useCallback((response) => {
        // Hard kick: 403 with SESSION_CONFLICT
        if (response.status === 403) {
            response.clone().json().then(data => {
                if (data?.detail && typeof data.detail === 'string' && data.detail.startsWith('SESSION_CONFLICT')) {
                    console.warn('[SessionEnforcement] Session conflict detected — logging out');
                    setSessionConflict(true);
                }
            }).catch(() => {});
            return;
        }

        // Soft warning: grace period headers
        const warning = response.headers.get('X-Session-Warning');
        if (warning === 'true') {
            const seconds = parseInt(response.headers.get('X-Session-Warning-Seconds') || '60', 10);
            setSessionWarning({ secondsRemaining: seconds });
        }
    }, []);

    const fetchMe = async (authToken) => {
        try {
            const response = await fetch('/api/auth/me', {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'X-Device-ID': deviceId,
                }
            });

            checkSessionResponse(response);

            if (response.ok) {
                const userData = await response.json();
                setUser(userData);
                localStorage.setItem('stratiq_user', JSON.stringify(userData));

                // PostHog Identify
                if (userData.email) {
                    posthog.identify(userData.email, {
                        email: userData.email,
                        role: userData.role
                    });
                }
            } else if (response.status === 401) {
                logout();
            }
        } catch (error) {
            console.error('Failed to sync user profile:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // Initialize from local storage
        if (token) {
            // Migration Fix: Clear legacy placeholder if present
            if (token === 'session_token_placeholder') {
                console.warn("Clearing legacy token...");
                logout();
                return;
            }

            // Sync with backend on mount to ensure latest state (onboarding, role, etc)
            fetchMe(token);
        } else {
            setIsLoading(false);
        }
    }, [token]);

    const login = async (googleToken) => {
        try {
            const response = await fetch('/api/auth/google', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Device-ID': deviceId,
                },
                body: JSON.stringify({ token: googleToken }),
            });

            if (!response.ok) {
                throw new Error('Login failed');
            }

            const data = await response.json();

            // Update State
            setToken(data.token);
            setUser(data.user);
            setSessionConflict(false);
            setSessionWarning(null);

            // Persist
            localStorage.setItem('stratiq_token', data.token);
            localStorage.setItem('stratiq_user', JSON.stringify(data.user));

            // PostHog Identify
            posthog.identify(data.user.email, {
                id: data.user.id,
                email: data.user.email,
                role: data.user.role,
                trial_active: data.user.trial_active
            });

            return true;
        } catch (error) {
            console.error('Login error:', error);
            return false;
        }
    };

    const logout = () => {
        setToken(null);
        setUser(null);
        setSessionConflict(false);
        setSessionWarning(null);
        localStorage.removeItem('stratiq_token');
        localStorage.removeItem('stratiq_user');

        posthog.reset(); // Clear PostHog session

        window.location.href = '/'; // Redirect cleanly to home screen
    };

    const syncUser = async () => {
        if (token) {
            await fetchMe(token);
        }
    };

    return (
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <AuthContext.Provider value={{
                user, token, login, logout, isLoading, syncUser,
                deviceId, sessionConflict, sessionWarning,
                checkSessionResponse,
            }}>
                {children}
            </AuthContext.Provider>
        </GoogleOAuthProvider>
    );
}
